import { DateTime } from "luxon";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { Subscription } from "@pages/about-products/about-products-filter-options";
import {
  ABOUT_PRODUCTS_TABLE_COLUMN_LABELS,
  AboutProductsTableEntity,
} from "@pages/about-products/about-products-table-types";
import { ExportRow } from "@utils/file-export";

/** The headers of the feature columns, which always come first in the file */
const FEATURE_HEADERS = {
  featureName: ABOUT_PRODUCTS_TABLE_COLUMN_LABELS.featureName,
  featureIdentity: ABOUT_PRODUCTS_TABLE_COLUMN_LABELS.featureIdentity,
  featureDescription: "Feature Description",
};

/** A subscription or bundle exported as one availability column */
interface ProductColumn {
  /** The field holding the availability in the table entity */
  field: string;

  /** The header of the column */
  header: string;
}

/**
 * Builds the availability columns. A header has to be unique, as it is the key of the exported
 * row, so a name used more than once is suffixed with the kind and id of its product.
 */
function getProductColumns(
  subscriptions: Subscription[],
  bundles: FaroCoreApiTypes.AdmBundle[]
): ProductColumn[] {
  const columns = [
    ...subscriptions.map(({ id, name }) => ({
      field: id,
      name,
      qualifier: `Subscription ${id}`,
    })),
    ...bundles.map(({ id, name, identifier }) => ({
      field: id,
      name,
      qualifier: `Bundle ${identifier}`,
    })),
  ];

  const nameCounts = new Map<string, number>(
    Object.values(FEATURE_HEADERS).map((header) => [header, 1])
  );
  for (const { name } of columns) {
    nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
  }

  return columns.map(({ field, name, qualifier }) => ({
    field,
    header: (nameCounts.get(name) ?? 0) > 1 ? `${name} (${qualifier})` : name,
  }));
}

/**
 * @param entities the rows of the about-products table
 * @param subscriptions the subscriptions selected in the filter
 * @param bundles the bundles selected in the filter
 * @returns one row per feature sorted by name, marking with Yes or No whether each selected
 * subscription and bundle includes it
 */
export function getAboutProductsExportRows(
  entities: AboutProductsTableEntity[],
  subscriptions: Subscription[],
  bundles: FaroCoreApiTypes.AdmBundle[]
): ExportRow[] {
  const productColumns = getProductColumns(subscriptions, bundles);

  return [...entities]
    .sort((a, b) => a.featureName.localeCompare(b.featureName))
    .map((entity) => {
      const row: ExportRow = {
        [FEATURE_HEADERS.featureName]: entity.featureName,
        [FEATURE_HEADERS.featureIdentity]: entity.featureIdentity,
        [FEATURE_HEADERS.featureDescription]: entity.featureDescription,
      };

      for (const { field, header } of productColumns) {
        row[header] = entity[field] === true ? "Yes" : "No";
      }

      return row;
    });
}

/** @returns the name of the exported file, without extension, e.g. "Admin Panel Product List 2026-07-20" */
export function getAboutProductsExportFileName(
  date: DateTime = DateTime.now()
): string {
  return `Admin Panel Product List ${date.toFormat("yyyy-MM-dd")}`;
}
